import { MatchCycle } from './match.interfaces';
import { CalendarProviderKey } from '@libs/src/calendar/calendar.types';
import { Locale } from '@libs/src/locale/locale.interface';
import { Account } from '@libs/src/models/account.model';
import { TENANT_PARTNER_TYPES } from '@libs/src/tenant/tenant-partner-types.enum';
import { TableData } from '@main-client/src/app/group-data-view-beta/group-data-view.helper';

export const msteamsMessagingMethod = 'msteams';
export const slackMessagingMethod = 'slack';

export interface Group {
  _id?: string;
  approved_domains?: string[];
  background_color?: string[];
  background_image_url?: string;
  community_page_information?: {
    attachments?: string[];
    description?: string;
    featured_accounts?: Account[];
    is_visible?: boolean;
    thumbnail?: {
      imageAlt?: string;
      imageUrl?: string;
      videoUrl?: string;
    };
  };
  content?: string;
  contract_info?: {
    total_licenses?: number;
  };
  conversation_goals_enabled?: boolean;
  created_at?: string;
  currentMatchCycle?: MatchCycle;
  custom_segments?: GroupCustomSegment[];
  development_programs_enabled?: boolean;
  emailSettings?: GroupEmailSetting[];
  features_enabled?: {
    events?: boolean;
    experimental_development_programs?: boolean;
    goals?: boolean;
    introductions?: boolean;
    localization?: boolean;
    pathways?: boolean;
  };
  field_descriptions?: GroupTopicsDescriptions[];
  group_type?: string;
  hidden_custom_segments?: GroupHiddenCustomSegment[];
  inactive_opt_out_day_range?: number;
  inactive_opt_out_enabled?: boolean;
  industries?: string[];
  integrations?: {
    calendar?: {
      provider?: CalendarProviderKey;
      use_msteams_native_calendar?: boolean;
    };
    messaging_method?:
      | typeof msteamsMessagingMethod
      | typeof slackMessagingMethod;
    msteams?: {
      channel_id: string;
      enabled: boolean;
      team_id: string;
    };
    slack?: {
      channel_id: string;
      channel_name: string;
      is_private: boolean;
    };
    workos?: {
      directory_sync_csv_enabled?: boolean;
    };
  };
  invisible?: boolean;
  invitation_email_campaigns_enabled?: boolean;
  invitation_email_config?: {
    custom_segment?: string;
    segment_type?: 'roles' | 'custom_segment' | 'hidden_custom_segment';
  };
  is_role_selectable_for_user_introduction_goals?: boolean;
  join_properties?: { header: string; name: string }[];
  join_type?: string;
  login_redirect?: boolean;
  logo_url?: string;
  match_start_date?: string;
  matchmaking_enabled?: boolean;
  members_rsvp_limit?: number;
  name?: string;
  off_platform?: boolean;
  offerings?: string[];
  partner_type?: TENANT_PARTNER_TYPES;
  roles?: GroupRole[];
  setup_checklist?: {
    appearance: boolean;
    base_config: boolean;
    community_page: boolean;
    features: boolean;
    introductions: boolean;
    invite_email: boolean;
    members_config: boolean;
    who_can_join: boolean;
  };
  slug?: string;
  status?: string;
  supported_locales?: Locale['code'][];
}

export interface GroupRole {
  _id?: string;
  defaults?: {
    expert_status?: string;
    inviteable?: boolean;
    opted_into_matchmaking?: boolean;
    searchable?: boolean;
  };
  name?: string;
}

export type GroupCustomSegmentTypes =
  | GroupCustomSegment
  | GroupHiddenCustomSegment;

export type GroupCustomSegmentSelectionTypes = 'single' | 'multi';

export interface GroupCustomSegment extends DefaultInteractionSettings {
  _id?: string;
  description?: string;
  nodes?: GroupCustomSegmentNode[];
  privacy_settings?: {
    editable_by_self: boolean;
    visible_in_onboarding: boolean;
  };
  title?: string;
}

export interface DefaultInteractionSettings {
  is_selectable_for_user_introduction_goals?: boolean;
  selection_type?: GroupCustomSegmentSelectionTypes;
}

export interface GroupCustomSegmentNode {
  _id?: string;
  name?: string;
}

export interface GroupTopicsDescriptions {
  _id?: string;
  description?: string;
  name?: string;
}

export interface GroupLocale {
  _id?: string;
  group: string;
  locale_name: string;
  mappings: { [key: string]: string };
  translation_unit: string;
}

export interface GroupHiddenCustomSegment extends GroupCustomSegment {
  privacy_settings?: {
    viewable_by_self: boolean;
    visible_in_onboarding: boolean;
    editable_by_self: boolean;
  };
}

export interface DataSegmentSelection {
  data: TableData[];
}

export type GroupCustomSegmentType =
  | 'custom_segments'
  | 'hidden_custom_segments';

export interface GroupEmailSetting {
  templateName: string;
}
